/**
 * RBAC — perfis e permissões.
 *
 * Regra de ouro: permissões são strings `recurso:ação`. Guards da API resolvem
 * as permissões efetivas do usuário a partir dos papéis atribuídos (globais na
 * organização ou escopados a um cliente específico).
 */

export const RoleKey = {
  SUPERADMIN: 'SUPERADMIN',
  AGENCY_ADMIN: 'AGENCY_ADMIN',
  TRAFFIC_MANAGER: 'TRAFFIC_MANAGER',
  COPYWRITER: 'COPYWRITER',
  DESIGNER: 'DESIGNER',
  ANALYST: 'ANALYST',
  SALES: 'SALES',
  CLIENT_VIEWER: 'CLIENT_VIEWER',
  CLIENT_APPROVER: 'CLIENT_APPROVER',
} as const;
export type RoleKey = (typeof RoleKey)[keyof typeof RoleKey];

export const Permission = {
  // organização e usuários
  ORG_MANAGE: 'org:manage',
  USER_READ: 'user:read',
  USER_MANAGE: 'user:manage',
  ROLE_ASSIGN: 'role:assign',

  // clientes e base de conhecimento
  CLIENT_READ: 'client:read',
  CLIENT_MANAGE: 'client:manage',
  PLAN_READ: 'plan:read',
  PLAN_MANAGE: 'plan:manage',
  BRAND_READ: 'brand:read',
  BRAND_MANAGE: 'brand:manage',

  // integrações
  INTEGRATION_READ: 'integration:read',
  INTEGRATION_MANAGE: 'integration:manage',

  // IA
  AI_GENERATE: 'ai:generate',
  AI_COST_READ: 'ai:cost:read',
  PROMPT_MANAGE: 'prompt:manage',

  // campanhas
  CAMPAIGN_READ: 'campaign:read',
  CAMPAIGN_DRAFT: 'campaign:draft',
  CAMPAIGN_PUBLISH: 'campaign:publish',
  CAMPAIGN_ACTIVATE: 'campaign:activate',
  BUDGET_MANAGE: 'budget:manage',

  // criativos
  CREATIVE_READ: 'creative:read',
  CREATIVE_MANAGE: 'creative:manage',
  TEMPLATE_MANAGE: 'template:manage',

  // social orgânico
  SOCIAL_READ: 'social:read',
  SOCIAL_MANAGE: 'social:manage',
  SOCIAL_PUBLISH: 'social:publish',

  // conteúdo / blog
  CONTENT_READ: 'content:read',
  CONTENT_MANAGE: 'content:manage',
  CONTENT_PUBLISH: 'content:publish',

  // métricas e relatórios
  METRIC_READ: 'metric:read',
  REPORT_READ: 'report:read',
  REPORT_MANAGE: 'report:manage',

  // conversões / CRM
  CONVERSION_READ: 'conversion:read',
  CONVERSION_MANAGE: 'conversion:manage',

  // otimização
  RECOMMENDATION_READ: 'recommendation:read',
  RECOMMENDATION_APPLY: 'recommendation:apply',

  // aprovações
  APPROVAL_READ: 'approval:read',
  APPROVAL_DECIDE: 'approval:decide',
  APPROVAL_DECIDE_CLIENT: 'approval:decide:client',

  // auditoria
  AUDIT_READ: 'audit:read',
} as const;
export type Permission = (typeof Permission)[keyof typeof Permission];

export const ALL_PERMISSIONS: Permission[] = Object.values(Permission);

const READ_ONLY_CLIENT: Permission[] = [
  Permission.CLIENT_READ,
  Permission.PLAN_READ,
  Permission.BRAND_READ,
  Permission.CAMPAIGN_READ,
  Permission.CREATIVE_READ,
  Permission.SOCIAL_READ,
  Permission.CONTENT_READ,
  Permission.METRIC_READ,
  Permission.REPORT_READ,
  Permission.APPROVAL_READ,
];

export const ROLE_PERMISSIONS: Record<RoleKey, Permission[]> = {
  SUPERADMIN: ALL_PERMISSIONS,

  AGENCY_ADMIN: ALL_PERMISSIONS.filter((p) => p !== Permission.ORG_MANAGE),

  TRAFFIC_MANAGER: [
    Permission.CLIENT_READ,
    Permission.PLAN_READ,
    Permission.BRAND_READ,
    Permission.INTEGRATION_READ,
    Permission.AI_GENERATE,
    Permission.CAMPAIGN_READ,
    Permission.CAMPAIGN_DRAFT,
    Permission.CAMPAIGN_PUBLISH,
    Permission.CAMPAIGN_ACTIVATE,
    Permission.BUDGET_MANAGE,
    Permission.CREATIVE_READ,
    Permission.CREATIVE_MANAGE,
    Permission.SOCIAL_READ,
    Permission.CONTENT_READ,
    Permission.METRIC_READ,
    Permission.REPORT_READ,
    Permission.REPORT_MANAGE,
    Permission.CONVERSION_READ,
    Permission.RECOMMENDATION_READ,
    Permission.RECOMMENDATION_APPLY,
    Permission.APPROVAL_READ,
    Permission.APPROVAL_DECIDE,
  ],

  COPYWRITER: [
    Permission.CLIENT_READ,
    Permission.PLAN_READ,
    Permission.BRAND_READ,
    Permission.AI_GENERATE,
    Permission.CAMPAIGN_READ,
    Permission.CAMPAIGN_DRAFT,
    Permission.CREATIVE_READ,
    Permission.SOCIAL_READ,
    Permission.SOCIAL_MANAGE,
    Permission.CONTENT_READ,
    Permission.CONTENT_MANAGE,
    Permission.METRIC_READ,
    Permission.APPROVAL_READ,
  ],

  DESIGNER: [
    Permission.CLIENT_READ,
    Permission.PLAN_READ,
    Permission.BRAND_READ,
    Permission.BRAND_MANAGE,
    Permission.AI_GENERATE,
    Permission.CREATIVE_READ,
    Permission.CREATIVE_MANAGE,
    Permission.TEMPLATE_MANAGE,
    Permission.SOCIAL_READ,
    Permission.CONTENT_READ,
    Permission.APPROVAL_READ,
  ],

  ANALYST: [
    Permission.CLIENT_READ,
    Permission.PLAN_READ,
    Permission.CAMPAIGN_READ,
    Permission.CREATIVE_READ,
    Permission.SOCIAL_READ,
    Permission.CONTENT_READ,
    Permission.METRIC_READ,
    Permission.REPORT_READ,
    Permission.REPORT_MANAGE,
    Permission.CONVERSION_READ,
    Permission.RECOMMENDATION_READ,
    Permission.AUDIT_READ,
    Permission.AI_COST_READ,
  ],

  SALES: [
    Permission.CLIENT_READ,
    Permission.PLAN_READ,
    Permission.METRIC_READ,
    Permission.REPORT_READ,
    Permission.CONVERSION_READ,
    Permission.CONVERSION_MANAGE,
  ],

  CLIENT_VIEWER: READ_ONLY_CLIENT,

  CLIENT_APPROVER: [...READ_ONLY_CLIENT, Permission.APPROVAL_DECIDE_CLIENT],
};

/** Papéis que só enxergam os clientes explicitamente vinculados ao usuário. */
export const CLIENT_SCOPED_ROLES: RoleKey[] = [RoleKey.CLIENT_VIEWER, RoleKey.CLIENT_APPROVER];

export function isClientScopedRole(role: RoleKey): boolean {
  return CLIENT_SCOPED_ROLES.includes(role);
}

export function permissionsForRoles(roles: RoleKey[]): Permission[] {
  const set = new Set<Permission>();
  for (const role of roles) {
    for (const permission of ROLE_PERMISSIONS[role] ?? []) set.add(permission);
  }
  return [...set];
}

export const ROLE_LABELS: Record<RoleKey, string> = {
  SUPERADMIN: 'Superadministrador',
  AGENCY_ADMIN: 'Admin da agência',
  TRAFFIC_MANAGER: 'Gestor de tráfego',
  COPYWRITER: 'Redator',
  DESIGNER: 'Designer',
  ANALYST: 'Analista',
  SALES: 'Comercial',
  CLIENT_VIEWER: 'Cliente (visualizador)',
  CLIENT_APPROVER: 'Cliente (aprovador)',
};
